import { Feather, SignOut } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { authApi } from "@/lib/apiClient";
import { markAnon } from "@/lib/authMarker";
import { BrandMark, BRAND_TEAL } from "@/components/BrandLogo";

// RM delegated session (2026-06) — persistent strip across every
// protected page. Exit kills the delegated server session AND clears
// the client auth marker so the RM is cleanly logged out of the
// delegated context.
function RmSessionBanner({ employer }) {
    const [busy, setBusy] = useState(false);
    const exit = async () => {
        setBusy(true);
        try { await authApi.logout(); } catch { /* session may already be revoked */ }
        markAnon();
        window.location.href = "/bmi-ops";
    };
    return (
        <div data-testid="rm-session-banner"
            className="sticky top-0 z-[60] flex items-center justify-between gap-3 bg-amber-500 text-amber-950 px-4 py-1.5 text-xs font-medium">
            <span data-testid="rm-session-banner-text">
                Delegated RM session — {employer.rm_email} acting on{" "}
                <strong>{employer.company_name || employer.email}</strong>. Payments are disabled.
            </span>
            <button data-testid="rm-session-exit-btn" disabled={busy} onClick={exit}
                className="inline-flex items-center gap-1.5 bg-amber-950 text-amber-50 rounded-sm px-3 py-1 text-[11px] font-semibold hover:opacity-90 disabled:opacity-50">
                <SignOut size={13} weight="bold" /> Exit account
            </button>
        </div>
    );
}

export function ProtectedRoute({ children, requirePlan = false }) {
    const { employer, loading, refreshMe } = useAuth();
    const location = useLocation();
    // 2026-06 RM-enter fix — NEVER bounce to /login off a stale
    // localStorage marker alone. If the context says "not authed"
    // (which can come from the marker short-circuit without any
    // network call), ask the SERVER once via /api/auth/me. Only a
    // real 401 sends the user to /login.
    const [serverRechecked, setServerRechecked] = useState(false);
    const recheckStarted = useRef(false);
    useEffect(() => {
        if (!loading && employer === false && !recheckStarted.current) {
            recheckStarted.current = true;
            refreshMe().finally(() => setServerRechecked(true));
        }
    }, [loading, employer, refreshMe]);
    if (loading || employer === null) return <FullPageSpinner />;
    if (!employer) {
        if (!serverRechecked) return <FullPageSpinner />;
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }
    // Google users without company info
    if (!employer.company_name && location.pathname !== "/complete-profile") {
        return <Navigate to="/complete-profile" replace />;
    }
    // Plan required? A legacy plan OR an active billing-v3 subscription both
    // satisfy the gate. When billing_v3_enabled is OFF no v3 subscription can
    // exist (the v3 endpoints 404), so legacy behaviour is byte-for-byte unchanged.
    const hasV3Subscription = !!(employer.subscription && employer.subscription.plan_code);
    if (requirePlan && !employer.plan && !hasV3Subscription && location.pathname !== "/plans") {
        return <Navigate to="/plans" replace />;
    }
    // Batch 3 (2026-02-28) — the restricted INTERVIEWER role lands on
    // /interviewer, not the main dashboard. They can also visit the
    // account-neutral pages (accept-invite, logout, plans) but every
    // /dashboard/* route redirects them home.
    // Batch 16 (2026-03-02) — additionally allow the pipeline page for
    // the INTERVIEWER role. The backend scopes /pipeline/{job_id} to
    // their assigned candidates only (see pipeline_routes.get_pipeline),
    // and every WRITE endpoint under /dashboard/* still 403s via
    // forbid_interviewer — so this widening is READ-ONLY by design.
    const isPipelineRoute = /^\/dashboard\/jobs\/[^/]+\/pipeline$/.test(location.pathname);
    if (employer.team_role === "INTERVIEWER"
        && !location.pathname.startsWith("/interviewer")
        && !isPipelineRoute
        && location.pathname !== "/complete-profile"
        && location.pathname !== "/plans") {
        return <Navigate to="/interviewer" replace />;
    }
    if (employer.is_rm_session) {
        return (
            <>
                <RmSessionBanner employer={employer} />
                {children}
            </>
        );
    }
    return children;
}

export function FullPageSpinner() {
    return (
        <div className="min-h-screen w-full grid place-items-center bg-background">
            <div className="flex items-center gap-3 text-muted-foreground">
                <Feather size={22} weight="duotone" className="text-primary animate-pulse" />
                <span className="text-sm uppercase tracking-[0.28em]">Loading…</span>
            </div>
        </div>
    );
}

export function AuthShell({ children, title, subtitle, testid }) {
    return (
        <div data-testid={testid} className="min-h-screen w-full flex bg-background">
            {/* Left brand pane */}
            <div className="hidden md:flex flex-col justify-between w-[42%] max-w-xl bg-foreground text-background p-10 lg:p-14 relative overflow-hidden">
                <div className="relative z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-sm bg-background/95 flex items-center justify-center shadow-sm">
                            <BrandMark size={30} />
                        </div>
                        <div>
                            <div className="font-sans font-extrabold text-2xl leading-none tracking-tight">
                                bookmyinterview<span className="text-primary">.ai</span>
                            </div>
                            <div
                                className="text-[10px] uppercase tracking-[0.32em] mt-1 font-semibold"
                                style={{ color: BRAND_TEAL }}
                            >
                                Hire. Faster.
                            </div>
                        </div>
                    </div>
                </div>
                <div className="relative z-10">
                    <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] mb-6">
                        Design the role.<br/>
                        <span className="text-primary/90">Draft the Job Description.</span><br/>
                        Book the interviews.
                    </h2>
                    <p className="text-background/70 text-base leading-relaxed max-w-md">
                        A consultant-grade platform that helps you write high-signal job descriptions —
                        with OCEAN personality maps, hidden performance traits, and a weighted screening rubric.
                        Then publish the role, share the link, and start booking interviews.
                    </p>
                </div>
                <div className="relative z-10 text-[10px] uppercase tracking-[0.28em] text-background/50">
                    © bookmyinterview.ai — Hire. Faster.
                </div>
                {/* decorative */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-1/3 -right-24 w-96 h-96 rounded-full bg-primary/25 blur-3xl" />
                    <div className="absolute bottom-0 -left-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
                </div>
            </div>

            {/* Right form pane */}
            <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <div className="mb-8">
                        {title && (
                            <h1 className="font-serif text-3xl sm:text-4xl leading-tight mb-2">{title}</h1>
                        )}
                        {subtitle && (
                            <p className="text-sm text-muted-foreground leading-relaxed">{subtitle}</p>
                        )}
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
}

export function LabeledField({ label, children, hint }) {
    return (
        <label className="block mb-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
                {label}
            </div>
            {children}
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </label>
    );
}

export function TextInput(props) {
    return (
        <input
            {...props}
            className={`w-full bg-background border border-border rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all ${props.className || ""}`}
        />
    );
}

export function SelectInput({ options, ...props }) {
    return (
        <select
            {...props}
            className={`w-full bg-background border border-border rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all ${props.className || ""}`}
        >
            <option value="">Select…</option>
            {options.map((o) => (
                <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
            ))}
        </select>
    );
}

export function PrimaryButton({ children, className = "", ...props }) {
    return (
        <button
            {...props}
            className={`w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm py-3 rounded-sm hover:-translate-y-[1px] hover:shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none ${className}`}
        >
            {children}
        </button>
    );
}

export function GhostButton({ children, className = "", ...props }) {
    return (
        <button
            {...props}
            className={`inline-flex items-center justify-center gap-2 border border-border bg-background hover:bg-muted text-sm py-2.5 px-3 rounded-sm transition-all ${className}`}
        >
            {children}
        </button>
    );
}

export const INDUSTRIES = [
    "Fintech", "Enterprise SaaS", "Consumer Internet", "E-commerce", "AI / ML",
    "Cybersecurity", "Healthcare / Healthtech", "Edtech", "Manufacturing", "Retail",
    "Media / Publishing", "Real Estate", "Logistics / Supply Chain", "Consulting",
    "Recruitment Tech", "Other",
];

export const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];

/** REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
export function googleLoginRedirect() {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
